import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ToastComponent } from '../../shared/toast/toast.component';
import { ClientService } from '../../services/client.service';
import { AuthService } from '../../services/auth.service';
import { RecordService } from '../../services/record.service';
import { Client } from '../../shared/models/client.model';
import { Record } from '../../shared/models/record.model';
import { ee, perPage50 } from '../../shared/constants';
import { removeTooltipElement, formatNotes, capitalizeFirstChar, isPastDate, sortArrayByDateFieldDesc } from '../../shared/functions';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-client-details',
  templateUrl: './client-details.component.html',
  styleUrls: ['./client-details.component.scss'],
  standalone: false
})
export class ClientDetailsComponent implements OnInit, OnDestroy {
  auth = inject(AuthService);
  private clientService = inject(ClientService);
  private recordService = inject(RecordService);
  toast = inject(ToastComponent);
  private router = inject(Router);
  private route = inject(ActivatedRoute);


  isLoading = {
    client: true,
    records: true,
    summary: false,
  };
  isAddModalVisible = {
    contract: false,
    goal: false,
  };
  isEditClientDetailsModalVisible = false;
  currentYear = new Date().getFullYear().toString();
  currentReviewYear = (new Date().getFullYear() - 1).toString();
  reviewYears = ['2021', '2022', '2023', '2024'];
  perPage = perPage50;
  isPastDate = isPastDate;
  formatNotes = formatNotes;
  // Client
  id = '';
  client: Client;
  field = '';
  summary = '';
  // Arrays
  clients: Client[] = [];
  records: Record[] = [];
  count = 0;
  page = 0;
  // Stats
  counters = { months: [], avg: 0 };
  barChartWeek;
  barChartActivity;
  currentContent = 'track-record';
  menuItems = [
    { name: 'Info & Records', link: 'track-record', icon: 'list' },
    { name: 'Contracts', link: 'contracts', icon: 'file-text-o', permission: 'contracts' },
    { name: 'Goals', link: 'goals', icon: 'bullseye' },
    { name: 'Yearly reviews', link: 'reviews', icon: 'commenting' },
  ];

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id');
    ee.on('changeContent', (data: string) => this.currentContent = data);
    ee.on('getRecords', (page?: number) => this.getRecords(this.id, page));
    ee.on('getClient', () => this.getClient(this.id));
    this.getClient(this.id);
    this.getClients();
    this.getRecords(this.id);
    // this.getRecordSummary(this.id); // Disabled since not liked, also budget on OpenAI is 0 now
  }

  ngOnDestroy(): void {
    ee.off('changeContent', () => {});
    ee.off('getRecords', () => {});
    ee.off('getClient', () => {});
    removeTooltipElement();
    this.barChartWeek?.destroy();
    this.barChartActivity?.destroy();
  }

  getClient(id: string): void {
    this.isLoading.client = true;
    this.clientService.get(id)
      .pipe(finalize(() => this.isLoading.client = false ))
      .subscribe({
        next: res => {
          this.client = res;
          // Sort contracts and goals by date DESC just for frontend
          this.client.contracts = sortArrayByDateFieldDesc(this.client.contracts, 'endDate');
          this.client.goals = sortArrayByDateFieldDesc(this.client.goals, 'dueDate');
        },
        error: err => {
          this.router.navigate(['/clients']);
          this.toast.setMessage(err.error.message, 'danger');
        }
      });
  }

  getClients(): void {
    this.clientService.search({ active: '1' })
      .subscribe({
        next: res => this.clients = res.results,
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getRecords(id: string, page?: number): void {
    this.isLoading.records = true;
    this.page = page || 0;
    this.recordService.search({
      _clients: id,
      page: this.page,
      perPage: this.perPage,
    })
      .pipe(finalize(() => this.isLoading.records = false))
      .subscribe({
        next: res => {
          this.records = res.results;
          this.count = res.count;
        },
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getRecordSummary(id: string): void {
    this.isLoading.summary = true;
    this.recordService.clientSummary(id, this.currentReviewYear)
      .pipe(finalize(() => this.isLoading.summary = false))
      .subscribe({
        next: res => this.summary = res,
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  toggleEditArrayFieldModal(field: 'contract' | 'goal'): void {
    this.isAddModalVisible[field] = true;
  }

  toggleEditClientDetailsModal(field: string, item?: any): void {
    this.isEditClientDetailsModalVisible = true;
    this.field = field;
    ee.emit('showEditClientDetailsModal', field, item);
  }

  deleteArrayField(itemId: string | number, field: 'contract' | 'goal'): void {
    if (window.confirm(`Are you sure you want to remove this ${field}?`)) {
      const client: Client = {
        _id: this.id
      };
      this.clientService.deleteArrayField(client, itemId, field)
        .pipe(finalize(() => {
          this.getClient(this.id);
          removeTooltipElement();
        }))
        .subscribe({
          next: res => this.toast.setMessage(`${capitalizeFirstChar(field)} deleted successfully`, 'success'),
          error: err => this.toast.setMessage(err.error.message, 'danger'),
        });
    }
  }

  selectReviewYear(year: string): void {
    this.currentReviewYear = year;
  }

}
